import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Rocket, ExternalLink, History, StickyNote, CheckCircle2, Circle,
  Pencil, RefreshCw, ListChecks, Layers, CloudUpload, ClipboardCheck, ShieldCheck, BadgeCheck,
} from "lucide-react";
import CertificationDialog from "../components/release/CertificationDialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const findMeta = (list, key) => (list || []).find((s) => s.key === key) || { label: key, color: "#94A3B8" };

function StatusBadge({ meta, prefix }) {
  return (
    <span
      data-testid={`status-badge-${prefix}`}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
      style={{ backgroundColor: `${meta.color}1A`, color: meta.color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="relative overflow-hidden border-slate-200 p-5">
      <div className="absolute -right-4 -top-4 opacity-[0.06]">
        <Icon size={90} strokeWidth={1.5} />
      </div>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        <Icon size={15} style={{ color: accent }} /> {label}
      </div>
      <div className="mt-2 font-head text-3xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </Card>
  );
}

export default function ReleaseDashboard() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [modules, setModules] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editMod, setEditMod] = useState(null);
  const [histMod, setHistMod] = useState(null);
  const [certMod, setCertMod] = useState(null);
  const [history, setHistory] = useState([]);
  const [noteText, setNoteText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [m, mods, ov] = await Promise.all([
        api.get("/release/meta"),
        api.get("/release/modules"),
        api.get("/release/overview"),
      ]);
      setMeta(m.data);
      setModules(mods.data);
      setOverview(ov.data);
    } catch (e) {
      toast.error("Errore nel caricamento della Release Dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const devMetaFor = (k) => findMeta(meta?.dev_statuses, k);

  const grouped = useMemo(() => {
    const g = {};
    modules.forEach((m) => {
      const c = m.category || "Generale";
      (g[c] = g[c] || []).push(m);
    });
    return g;
  }, [modules]);

  const saveEdit = async () => {
    try {
      await api.put(`/release/modules/${editMod.id}`, {
        dev_status: editMod.dev_status,
        test_status: editMod.test_status,
        approval_status: editMod.approval_status,
        environment: editMod.environment,
        checklist: editMod.checklist,
      });
      toast.success("Modulo aggiornato");
      setEditMod(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const openHistory = async (m) => {
    setHistMod(m);
    setNoteText("");
    try {
      const r = await api.get(`/release/modules/${m.id}/history`);
      setHistory(r.data);
    } catch { setHistory([]); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/release/modules/${histMod.id}/notes`, { note: noteText.trim() });
      toast.success("Nota di avanzamento aggiunta");
      setNoteText("");
      const r = await api.get(`/release/modules/${histMod.id}/history`);
      setHistory(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const toggleCheck = (idx) => {
    const cl = [...editMod.checklist];
    cl[idx] = { ...cl[idx], done: !cl[idx].done };
    setEditMod({ ...editMod, checklist: cl });
  };

  if (loading || !meta) {
    return <div className="py-20 text-center text-slate-400" data-testid="release-loading">Caricamento…</div>;
  }

  return (
    <div className="space-y-8" data-testid="release-dashboard">
      {/* Intro */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-indigo-600">
            <Rocket size={16} /> Urban OS · Control Room
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Stato reale di sviluppo, test, approvazione e ambiente di ogni modulo. Lo storico è collegato
            allo Storico movimenti (audit) e puoi aggiungere note di avanzamento manuali.
          </p>
        </div>
        <Button data-testid="release-refresh-btn" variant="outline" size="sm" className="gap-2 self-start" onClick={load}>
          <RefreshCw size={15} /> Aggiorna
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="release-kpis">
        <KpiCard icon={Layers} label="Moduli totali" value={overview?.total ?? 0}
          sub={`${overview?.completed ?? 0} completati`} accent="#2563EB" />
        <KpiCard icon={ClipboardCheck} label="Completamento" value={`${overview?.pct_completion ?? 0}%`}
          sub="moduli completati/pubblicati" accent="#059669" />
        <KpiCard icon={ListChecks} label="Checklist" value={`${overview?.pct_checklist ?? 0}%`}
          sub={`${overview?.checklist?.done ?? 0}/${overview?.checklist?.total ?? 0} voci`} accent="#7C3AED" />
        <KpiCard icon={CloudUpload} label="In produzione" value={overview?.in_production ?? 0}
          sub={`${overview?.in_preview ?? 0} in preview`} accent="#047857" />
      </div>

      {/* Status legend / distribution */}
      <Card className="border-slate-200 p-4">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Distribuzione per stato sviluppo</div>
        <div className="flex flex-wrap gap-2">
          {meta.dev_statuses.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-slate-600">{s.label}</span>
              <Badge variant="secondary" className="ml-1">{overview?.by_dev_status?.[s.key] || 0}</Badge>
            </span>
          ))}
        </div>
      </Card>

      {/* Modules by category */}
      {Object.entries(grouped).map(([cat, mods]) => (
        <div key={cat} className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-800">{cat}</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {mods.map((m) => {
              const done = (m.checklist || []).filter((c) => c.done).length;
              const total = (m.checklist || []).length;
              const pct = total ? Math.round((100 * done) / total) : 0;
              return (
                <Card key={m.id} data-testid={`release-module-${m.key}`} className="flex flex-col border-slate-200 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate font-head text-base font-semibold text-slate-900">{m.name}</h3>
                      <p className="mt-1 line-clamp-2 text-xs text-slate-500">{m.description}</p>
                    </div>
                    <div className="shrink-0">
                      <StatusBadge meta={devMetaFor(m.dev_status)} prefix={`dev-${m.key}`} />
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <StatusBadge meta={findMeta(meta.test_statuses, m.test_status)} prefix={`test-${m.key}`} />
                    <StatusBadge meta={findMeta(meta.approval_statuses, m.approval_status)} prefix={`appr-${m.key}`} />
                    <StatusBadge meta={findMeta(meta.environments, m.environment)} prefix={`env-${m.key}`} />
                    {m.certification?.certified && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700" data-testid={`cert-badge-${m.key}`}>
                        <BadgeCheck size={12} /> Certificato v{m.certification.version}
                      </span>
                    )}
                  </div>

                  <div className="mt-4">
                    <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                      <span>Checklist</span><span>{done}/{total}</span>
                    </div>
                    <Progress value={pct} className="h-1.5" />
                  </div>

                  <div className="mt-3 text-[11px] text-slate-400">Ultimo aggiornamento: {fmtDate(m.updated_at)}</div>

                  <Separator className="my-3" />
                  <div className="mt-auto flex flex-wrap gap-2">
                    {m.route && (
                      <Button data-testid={`open-module-${m.key}`} variant="outline" size="sm" className="gap-1.5"
                        onClick={() => navigate(m.route)}>
                        <ExternalLink size={14} /> Apri modulo
                      </Button>
                    )}
                    <Button data-testid={`history-module-${m.key}`} variant="ghost" size="sm" className="gap-1.5"
                      onClick={() => openHistory(m)}>
                      <History size={14} /> Storico
                    </Button>
                    <Button data-testid={`edit-module-${m.key}`} variant="ghost" size="sm" className="gap-1.5"
                      onClick={() => setEditMod({ ...m, checklist: (m.checklist || []).map((c) => ({ ...c })) })}>
                      <Pencil size={14} /> Modifica
                    </Button>
                    <Button data-testid={`certify-module-${m.key}`} variant="ghost" size="sm" className="gap-1.5 text-indigo-600"
                      onClick={() => setCertMod(m)}>
                      <ShieldCheck size={14} /> Certifica
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {certMod && (
        <CertificationDialog module={certMod} meta={meta} onClose={() => setCertMod(null)} onSaved={load} />
      )}

      {/* Edit dialog */}
      <Dialog open={!!editMod} onOpenChange={(o) => !o && setEditMod(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="release-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editMod?.name}</DialogTitle>
            <DialogDescription>Aggiorna stato sviluppo, test, approvazione, ambiente e checklist del modulo.</DialogDescription>
          </DialogHeader>
          {editMod && (
            <div className="space-y-4">
              {[
                ["dev_status", "Stato sviluppo", meta.dev_statuses],
                ["test_status", "Stato test", meta.test_statuses],
                ["approval_status", "Stato approvazione", meta.approval_statuses],
                ["environment", "Ambiente", meta.environments],
              ].map(([field, label, opts]) => (
                <div key={field}>
                  <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
                  <Select value={editMod[field]} onValueChange={(v) => setEditMod({ ...editMod, [field]: v })}>
                    <SelectTrigger data-testid={`edit-${field}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {opts.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}

              {(editMod.checklist || []).length > 0 && (
                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-600">Checklist</label>
                  <div className="space-y-2">
                    {editMod.checklist.map((c, i) => (
                      <label key={c.id || i} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                        <Checkbox data-testid={`edit-check-${i}`} checked={!!c.done} onCheckedChange={() => toggleCheck(i)} />
                        <span className={c.done ? "text-slate-400 line-through" : ""}>{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditMod(null)}>Annulla</Button>
            <Button data-testid="release-save-btn" onClick={saveEdit}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!histMod} onOpenChange={(o) => !o && setHistMod(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="release-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History size={18} /> Storico · {histMod?.name}</DialogTitle>
            <DialogDescription>Cronologia collegata allo Storico movimenti (audit) e note di avanzamento manuali.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600">Aggiungi nota di avanzamento manuale</label>
            <Textarea data-testid="release-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
              placeholder="Es. Rivisto flusso pagamenti con il commercialista…" rows={2} />
            <div className="flex justify-end">
              <Button data-testid="release-add-note-btn" size="sm" className="gap-1.5" onClick={addNote} disabled={!noteText.trim()}>
                <StickyNote size={14} /> Aggiungi nota
              </Button>
            </div>
          </div>

          <Separator />

          <div className="space-y-3" data-testid="release-history-list">
            {history.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Nessun evento registrato.</p>}
            {history.map((ev) => (
              <div key={ev.id} className="flex gap-3">
                <div className="mt-0.5">
                  {ev.type === "note"
                    ? <StickyNote size={16} className="text-amber-500" />
                    : <CheckCircle2 size={16} className="text-indigo-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">
                      {ev.type === "note" ? "Nota manuale" : ev.action?.replace(/_/g, " ")}
                    </span>
                    <span>·</span><span>{ev.user || "—"}</span><span>·</span><span>{fmtDate(ev.at)}</span>
                  </div>
                  {ev.note && <p className="mt-0.5 text-sm text-slate-700">{ev.note}</p>}
                  {ev.new && Object.keys(ev.new).length > 0 && (
                    <p className="mt-0.5 text-xs text-slate-500">
                      {Object.entries(ev.new).map(([k, v]) => (
                        <span key={k} className="mr-2">
                          <span className="text-slate-400">{k}:</span>{" "}
                          {ev.old?.[k] ? <><span className="line-through">{String(ev.old[k])}</span> → </> : null}
                          <span className="font-medium">{String(v)}</span>
                        </span>
                      ))}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
